// App.tsx
import React, { useEffect, useRef } from "react";
import { Linking } from "react-native";
import {
  NavigationContainer,
  useNavigationContainerRef,
} from "@react-navigation/native";
import { Provider } from "react-redux";
import { store } from "./src/redux/store";
import RootStack from "./src/navigation/RootStack";
import { AuthContainer } from "./src/AuthContainer";
import * as Sentry from "./src/lib/sentry";
import PWAInstallPrompt from "./src/components/PWAInstallPrompt";
import EnableNotificationsPrompt from "./src/components/EnableNotificationsPrompt";
import { useNotificationNavigation } from "./src/hooks/useNotificationNavigation";
import DeepLinkNavigator from "./src/components/DeepLinkNavigator";
import { setPendingRoom } from "./src/navigation/pendingRoom";
import { useFonts } from "expo-font";
import { appFonts } from "./src/constants/fonts";
import type RootStackParamList from "./src/navigation/RootStackParamList";
import { logScreenView } from "./src/utils/analyticsUtils";
import { StatusBar } from "expo-status-bar";
import { SafeAreaProvider } from "react-native-safe-area-context";

Sentry.init({
  dsn: "https://1ef006ffa5e93f13b3314ca72ec463e8@o4510068926054400.ingest.us.sentry.io/4510072736514048",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.feedbackIntegration({
      colorScheme: "system",
      autoInject: false,
    }),
  ].filter(Boolean),
});

export default function App() {
  const navigationRef = useNavigationContainerRef<RootStackParamList>();
  const routeNameRef = useRef<string | undefined>(undefined);
  const [fontsLoaded] = useFonts(appFonts);

  // Route notification taps (native). clickAction is a route name from the
  // push payload; fall back to the meetings list so a tap always lands somewhere.
  const handleNotificationNavigate = React.useCallback(
    (clickAction: string) => {
      if (!navigationRef.isReady()) return;
      const target = (clickAction ||
        "MeetingsTabs") as keyof RootStackParamList;
      try {
        navigationRef.navigate(target as never);
      } catch {
        navigationRef.navigate("MeetingsTabs" as never);
      }
    },
    [navigationRef],
  );
  useNotificationNavigation(handleNotificationNavigate);

  // Board #105: capture the room id from the "View your conversation" email link
  // (https://knexoapp.com/room/:roomId). DeepLinkNavigator opens the conversation
  // once the user is authenticated. On web Linking.getInitialURL() is the current
  // location; the listener covers a link opened while the app is already running.
  useEffect(() => {
    const capture = (url: string | null | undefined) => {
      const match = url?.match(/room\/([^/?#]+)/);
      if (match?.[1]) setPendingRoom(decodeURIComponent(match[1]));
    };
    Linking.getInitialURL()
      .then(capture)
      .catch(() => {});
    const sub = Linking.addEventListener("url", ({ url }) => capture(url));
    return () => sub.remove();
  }, []);

  if (!fontsLoaded) return null;

  return (
    <SafeAreaProvider>
      <Provider store={store}>
        <NavigationContainer
          ref={navigationRef}
          onReady={() => {
            const currentRouteName = navigationRef.getCurrentRoute()?.name;
            routeNameRef.current = currentRouteName;

            if (currentRouteName) {
              logScreenView(currentRouteName);
            }
          }}
          onStateChange={() => {
            const previousRouteName = routeNameRef.current;
            const currentRouteName = navigationRef.getCurrentRoute()?.name;

            if (currentRouteName && previousRouteName !== currentRouteName) {
              logScreenView(currentRouteName);
            }

            routeNameRef.current = currentRouteName;
          }}
        >
          <AuthContainer>
            <StatusBar style="dark" />
            <RootStack />
            <DeepLinkNavigator navigationRef={navigationRef} />
            <PWAInstallPrompt />
            <EnableNotificationsPrompt />
          </AuthContainer>
        </NavigationContainer>
      </Provider>
    </SafeAreaProvider>
  );
}
